import { useContext } from "react";
import styled, {
  css,
  DefaultTheme,
  FlattenInterpolation,
  ThemeProps,
} from "styled-components";
import {
  Alert,
  Alerts,
  AlertsContext,
  Flex,
  FormErrorTypes,
  useCheckIfSubscriptionFeatureActiveBroker,
  ViewerContext,
} from "dash-ui";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AppSideBar from "flow-components/AppSideBar/AppSideBar";
import BreadcrumbNavigation from "flow-components/BreadcrumbNavigation/BreadcrumbNavigation";
import InactiveSubscriptionBanner from "common/components/InactiveSubscriptionBanner/InactiveSubscriptionBanner";

import { IContainerProps } from "./Container.interfaces";
import { RoutesPath } from "flow-components/routes/routes";

const ContainerWrapper = styled(Flex)`
  align-self: center;
  justify-content: center;
  width: 100%;
  flex-grow: 0;
`;

const ContainerStyle = styled(Flex)<{ size: "xl" | "lg" }>`
  background-color: ${({ theme }) => theme.colors.b10};
  min-height: calc(100vh - 110px);
  min-width: 0;
  box-sizing: border-box;
  max-width: ${({ theme, size }) => theme.breakpoints[size]}px;

  @media ${({ theme }) => theme.devices.mobile} {
    padding-left: ${({ theme }) => theme.space * 4}px;
    padding-right: ${({ theme }) => theme.space * 4}px;
    min-height: calc(100vh - 210px);
  }
  @media ${({ theme }) => theme.devices.tablet} {
    padding-left: ${({ theme }) => theme.space * 6}px;
    padding-right: ${({ theme }) => theme.space * 6}px;
  }
`;
const BreadcrumbWrapper = styled(Flex)<{
  stickyBreadcrumbs: boolean;
  fullWidthOnMobile?: boolean;
}>`
  background-color: inherit;

  ${({ stickyBreadcrumbs }) =>
    stickyBreadcrumbs
      ? css`
          top: 50px;
          position: sticky;
          z-index: 4;
        `
      : ""}

  @media ${({ theme }) => theme.devices.mobile} {
    ${({ fullWidthOnMobile, theme }) =>
      fullWidthOnMobile &&
      css`
        margin-left: -${theme.space * 4}px;
        margin-right: -${theme.space * 4}px;
        width: calc(100% + ${theme.space * 8}px);
        padding-left: ${theme.space * 4}px;
        padding-right: ${theme.space * 4}px;
      `}
  }
`;
const Content = styled(Flex)<{
  css?: FlattenInterpolation<ThemeProps<DefaultTheme>> | string;
}>`
  background-color: ${({ theme }) => theme.colors.b10};
  width: 100%;
  box-sizing: border-box;
  ${({ css }) => css}
`;

function Container({
  children,
  contentStyle,
  withBreadcrumbs = true,
  breadcrumbsProps,
  withSidebar = true,
  showContent,
  size = "xl",
  className = "",
  fullWidthBreadcrumbsOnMobile = false,
}: IContainerProps) {
  const { t } = useTranslation("common");
  const { state } = useContext(AlertsContext);
  const { account } = useContext(ViewerContext);
  const isSeeker = account?.isSeeker;

  const location = useLocation();
  const { isSubscriptionActive } = useCheckIfSubscriptionFeatureActiveBroker();

  const pathsHiddenFromSeekers: string[] = [
    RoutesPath.Exchange,
    RoutesPath.Portfolios,
  ];
  const currentPath = location.pathname;
  const firstPathElement = currentPath.split("/")?.[1];

  if (isSeeker && pathsHiddenFromSeekers.includes(`/${firstPathElement}`)) {
    return <Navigate to={RoutesPath.Properties} />;
  }

  // For portfolios and exchange pages, always show content (landing page or regular page based on subscription)
  const isPortfoliosPage = currentPath.includes(RoutesPath.Portfolios);
  const isExchangePage = currentPath.includes(RoutesPath.Exchange);
  const isLandingEnabledPage = isPortfoliosPage || isExchangePage;

  const shouldShowBanner =
    !isSubscriptionActive && !showContent && !isLandingEnabledPage;
  const shouldShowContent =
    isSubscriptionActive || showContent || isLandingEnabledPage;

  return (
    <ContainerWrapper flexDirection="row" w="inherit" className={className}>
      {withSidebar && <AppSideBar />}

      <ContainerStyle
        flex={1}
        px={11}
        pt={6}
        pb={4}
        flexDirection="column"
        w="100%"
        size={size}
      >
        {shouldShowBanner && <InactiveSubscriptionBanner />}
        {shouldShowContent && (
          <>
            {withBreadcrumbs && (
              <BreadcrumbWrapper
                alignItems="center"
                justifyContent="space-between"
                w="100%"
                pt={5}
                pb={4}
                stickyBreadcrumbs={breadcrumbsProps?.sticky ?? false}
                fullWidthOnMobile={fullWidthBreadcrumbsOnMobile}
              >
                <BreadcrumbNavigation />
                {state[Alerts.ChangesSaved] && (
                  <Alert
                    type={FormErrorTypes.Valid}
                    alertType={Alerts.ChangesSaved}
                  >
                    {t("alerts.changesSaved")}
                  </Alert>
                )}
              </BreadcrumbWrapper>
            )}
            <Content flex={1} css={contentStyle}>
              {children}
            </Content>
          </>
        )}
      </ContainerStyle>
    </ContainerWrapper>
  );
}

export default Container;
